type IntlWithSupportedValues = typeof Intl & {
  supportedValuesOf?: (key: 'timeZone') => string[];
};

export type TimezoneOption = {
  value: string;
  label: string;
  offsetMinutes: number;
  featured?: boolean;
};

// Fort Liberty (EST) default to match USA SOC customer.
const DEFAULT_TIMEZONE = 'America/New_York';

const FEATURED_TIMEZONES = [
  DEFAULT_TIMEZONE,
  'UTC',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Asia/Singapore',
  'Asia/Kolkata',
  'Australia/Sydney',
];

const FALLBACK_TIMEZONES = [
  'UTC',
  'Etc/UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Paris',
  'Europe/Warsaw',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Asia/Singapore',
  'Asia/Manila',
  'Asia/Kolkata',
  'Australia/Sydney',
];

const getSupportedTimezones = () => {
  const intlWithSupportedValues = Intl as IntlWithSupportedValues;
  if (typeof intlWithSupportedValues.supportedValuesOf === 'function') {
    return intlWithSupportedValues.supportedValuesOf('timeZone');
  }

  return FALLBACK_TIMEZONES;
};

const parseOffsetMinutes = (offsetLabel: string): number => {
  // Examples: UTC, UTC+05:30, GMT-8
  const match = offsetLabel.match(/([+-])(\d{1,2})(?::?(\d{2}))?/);
  if (!match) return 0;

  const sign = match[1] === '-' ? -1 : 1;
  const hours = Number(match[2] ?? 0);
  const minutes = Number(match[3] ?? 0);
  return sign * (hours * 60 + minutes);
};

const getOffsetLabel = (timeZone: string): { label: string; minutes: number } => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
      hour12: false,
    })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName');

    const raw = parts?.value?.replace('GMT', 'UTC') ?? 'UTC';
    return { label: raw, minutes: parseOffsetMinutes(raw) };
  } catch {
    return { label: 'UTC', minutes: 0 };
  }
};

const buildOption = (timeZone: string, featured = false): TimezoneOption => {
  const { label, minutes } = getOffsetLabel(timeZone);
  return {
    value: timeZone,
    label: `${label} — ${timeZone}`,
    offsetMinutes: minutes,
    featured,
  };
};

export const buildTimezoneOptions = (preferred: string[] = []): TimezoneOption[] => {
  const seen = new Set<string>();
  const options: TimezoneOption[] = [];

  const add = (timeZone: string, featured = false) => {
    if (!timeZone || seen.has(timeZone)) return;
    seen.add(timeZone);
    options.push(buildOption(timeZone, featured));
  };

  FEATURED_TIMEZONES.forEach((tz) => add(tz, true));
  preferred.forEach((tz) => add(tz));
  getSupportedTimezones().forEach((tz) => add(tz));

  return options.sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (b.featured && !a.featured) return 1;
    if (a.offsetMinutes !== b.offsetMinutes) return a.offsetMinutes - b.offsetMinutes;
    return a.label.localeCompare(b.label);
  });
};

export const createTimezoneOption = (timeZone: string): TimezoneOption => buildOption(timeZone);

export const DEFAULT_TIMEZONE_ID = DEFAULT_TIMEZONE;
